import type { ComboboxItem } from '@any-ui-react/core';
import { type ForwardedRef, forwardRef, useEffect } from 'react';

import {
  type ListCurrenciesForPerformanceReviewQueryVariables,
  type ListCurrenciesQueryVariables,
  useListCurrenciesForPerformanceReviewQuery,
  useListCurrenciesQuery,
} from '@/common/graphql';

import type { CommonProps, UseQueryReturn } from '../props';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

const useQuery = (onLoadingCompleted?: () => void): UseQueryReturn<ListCurrenciesQueryVariables, ComboboxItem> => {
  const { data, ...rest } = useListCurrenciesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCurrencies?.items?.map(it => ({
        value: it.id,
        label: it.name,
        labelFormatted: (
          <div className="flex justify-between">
            <div>{it.id}</div>
            <div className="ml-auto hidden pl-2 text-right text-3xs text-gray-7 md:block">{it.name}</div>
          </div>
        ),
        disabled: false,
      })) || [],
    ...rest,
  };
};

const useQueryForPerformanceReview = (
  variables: ListCurrenciesForPerformanceReviewQueryVariables,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListCurrenciesForPerformanceReviewQueryVariables, ComboboxItem> => {
  const { data, ...rest } = useListCurrenciesForPerformanceReviewQuery({
    variables: {
      performanceReviewId: variables.performanceReviewId!,
    },
    skip: !variables.performanceReviewId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCurrenciesForPerformanceReview?.items?.map(it => ({
        value: it.id,
        label: it.name,
        labelFormatted: (
          <div className="flex flex-wrap gap-x-2 justify-between">
            <div>{it.id}</div>
            <div className="ml-auto text-3xs text-gray-7">{it.name}</div>
          </div>
        ),
        disabled: false,
      })) || [],
    ...rest,
  };
};

export const CurrencySelectSingle = forwardRef(
  (props: { onLoadingCompleted?: () => void } & SelectSingleProps<ComboboxItem>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const CurrencySelectSingleForPerformanceReview = forwardRef(
  (
    props: Omit<CommonProps<ListCurrenciesForPerformanceReviewQueryVariables>, 'variables'> & {
      variables: ListCurrenciesForPerformanceReviewQueryVariables;
    } & SelectSingleProps<ComboboxItem>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { data, loading } = useQueryForPerformanceReview(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);
